import React, { useState, useCallback } from 'react';
import { Table, Input, Typography, Button, AutoComplete } from 'antd';
import axios from 'axios';
import { DeleteOutlined } from '@ant-design/icons';
import { workerURL } from '../Config';
import { useAppContext } from './AppContext';

const SuccessorComponent = ({ user, successorData, setsuccessorData }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const [inputValues, setInputValues] = useState({}); // Local state for input values
  const [rowIdCounter, setRowIdCounter] = useState(0); // Counter for unique row IDs
  const { accessToken,idToken } = useAppContext();

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId;
      return (value, record) => {
        console.log('Debounced search called with:', value);
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          console.log('Executing search after delay for:', value);
          if (value && value.length >= 1) { // Changed from 2 to 1 for faster search
            handleSearch(value, record);
          }
        }, 500); // Increased delay to 500ms
      };
    })(),
    [accessToken] // Add dependency
  );

  const handleSearch = async (value, record) => {
    console.log('Search triggered for value:', value);
    setSearchTerm(value);
    setLoading(true);
    
    // Properly encode the search value and use a more specific filter
    const encodedValue = encodeURIComponent(value);
    const url = `https://graph.microsoft.com/v1.0/users?$filter=startswith(displayName,'${encodedValue}') or startswith(givenName,'${encodedValue}') or startswith(surname,'${encodedValue}')&$select=displayName,userPrincipalName,givenName,surname&$top=10`;
    console.log('API URL:', url);
    
    try {
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}` // Include access token in headers
        }
      });
      
      console.log('API Response:', response.data);
      
      // Filter results locally to ensure they actually match the search term
      const filteredUsers = response.data.value.filter(user => {
        const searchLower = value.toLowerCase();
        const displayNameMatch = user.displayName?.toLowerCase().includes(searchLower);
        const givenNameMatch = user.givenName?.toLowerCase().includes(searchLower);
        const surnameMatch = user.surname?.toLowerCase().includes(searchLower);
        
        return displayNameMatch || givenNameMatch || surnameMatch;
      });
      
      console.log('Filtered users:', filteredUsers);
      
      // Remove duplicates based on displayName and userPrincipalName
      const uniqueUsers = filteredUsers.filter((user, index, self) => 
        index === self.findIndex(u => u.displayName === user.displayName && u.userPrincipalName === user.userPrincipalName)
      );
      
      const suggestions = uniqueUsers.map(user => ({ value: user.displayName, user }));
      console.log('Processed suggestions:', suggestions);
      
      setSearchResults(suggestions);
      
      // Store suggestions separately without updating the main data array
      const recordKey = record.rowId || record.successorMailID || `legacy_${record.empName || 'empty'}`;
      setInputValues(prev => ({
        ...prev,
        [`${recordKey}_suggestions`]: suggestions
      }));
      
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (value, record, option) => {
    //console.log('option:', record);
    //console.log('value', value);
    //console.log('option', option);

    // Update local input value using stable row ID
    const recordKey = record.rowId || record.successorMailID || `legacy_${record.empName || 'empty'}`;
    setInputValues(prev => ({
      ...prev,
      [recordKey]: value
    }));

    fetch(workerURL + `/${option.user.userPrincipalName}`,{
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`  // Include the bearer token here
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        return response.json();
      })
      .then(data => {
        //setEmployeeData(data);
        //console.log('Data:', data);
        
        // Update the current record with selected employee data
        const updatedData = successorData.map(item => {
          if (item === record) {
            return {
              ...item,
              empName: value, // Set the selected employee name
              manager: data.managerName,
              title: data.jobTitle,
              readiness: data.readiness,
              successorMailID: data.userPrincipalName,
              empMailID: user.userPrincipalName
            };
          }
          return item;
        });
        
        setsuccessorData(updatedData);
      })
      .catch(error => {
        //console.error('Error fetching data:', error);
      });
  };



  const handleInputChange = (key, value) => {
    //console.log(value);
    //console.log(key);
    const newData = [...successorData];
    const index = newData.findIndex((item) => key === item.successorMailID);
    newData[index].comments = value;
    setsuccessorData(newData);
  };

  const addNewRow = () => {
    const newRowId = `row_${rowIdCounter}`;
    const newRow = {
      empName: '',
      empMailId: '',
      successorMailID: newRowId, // Use counter-based unique ID
      manager: '',
      title: '',
      readiness: '',
      comments: '',
      empnameSuggestions: [],
      isNewRow: true, // Flag to identify new rows
      rowId: newRowId // Store the row ID
    };

    // Increment counter for next row
    setRowIdCounter(prev => prev + 1);
    
    // Preserve existing data and add new row
    setsuccessorData(prevData => [...prevData, newRow]);
    
    // Don't clear inputValues - preserve existing input states
  };

  const columns = [


    {
      title: 'Employee Name',
      dataIndex: 'empName',
      key: 'empName',
      width: 200,
      render: (text, record) => {
        // Use stable row ID for consistent keys
        const recordKey = record.rowId || record.successorMailID || `legacy_${record.empName || 'empty'}`;
        const currentValue = inputValues[recordKey] !== undefined ? inputValues[recordKey] : (record.empName || '');
        const suggestions = inputValues[`${recordKey}_suggestions`] || [];
        
        return (
          <AutoComplete
            placeholder="Successor Not Identified"
            options={suggestions}
            value={currentValue}
            onSearch={(value) => {
              console.log('onSearch triggered with value:', value);
              
              // Update local input value immediately
              setInputValues(prev => ({
                ...prev,
                [recordKey]: value
              }));
              
              // Clear suggestions if input is empty
              if (!value.trim()) {
                console.log('Clearing suggestions for empty input');
                setInputValues(prev => ({
                  ...prev,
                  [`${recordKey}_suggestions`]: []
                }));
              } else {
                console.log('Calling debounced search for:', value);
                // Debounced search for suggestions
                debouncedSearch(value, record);
              }
            }}
            onSelect={(value, option) => handleSelect(value, record, option)}
            style={{
              width: 250,
            }}
          />
        );
      },
    },

    {
      title: 'Manager',
      dataIndex: 'manager',
      key: 'manager',
      width: 250,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: 200,
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      key: 'comments',
      render: (text, record) => (
        <Input.TextArea
          value={text}
          autoSize={{ minRows: 3, maxRows: 3 }} // Set the number of rows
          onChange={(e) => handleInputChange(record.successorMailID, e.target.value)}
          style={{ overflowY: 'auto', width: '300px' }}
        />
      ),
    },
    {
      title: 'Remove',
      key: 'action',
      width: 50,
      render: (text, record) => (
        <Button
          type="link"
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.successorMailID)}
        />
      ),
    }
  ];

  const handleDelete = (key) => {
    // Implement your delete logic here
    //console.log('Delete record with key:', key);
    setsuccessorData(successorData.filter((item) => item.successorMailID !== key));
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        marginBottom: '16px' 
      }}>
        <h2 style={{ margin: 0 }}>Successor for this role</h2>
        <Button 
          type="primary" 
          onClick={addNewRow}
          style={{
            borderRadius: '6px',
            fontWeight: '500'
          }}
        >
          ➕ Add Successor
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={successorData}
        bordered
        pagination={false}
        rowKey={(record, index) => record.rowId || record.successorMailID || `legacy_row_${index}`}
      />
    </div>

  );
};

export default SuccessorComponent;
